import { useEffect, useState } from 'react';
import {
  clearTenderTemplate,
  getAllTemplates,
  getUserTemplates,
  setTenderTemplate,
} from '../../services/templateService';
import type { User } from '../../types/auth';
import type { Template } from '../../types/template';
import type { Tender } from '../../types/tender';
import { logTenderActivity } from '../../utils/activityLogger';

const EMPTY_OBJECT: Record<string, never> = {};

export const useStructureTemplates = (
  tender: Tender | null,
  account: (User & { clientId?: string; aiSettings?: unknown }) | null,
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void
) => {
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<Record<string, string>>({
    draft: 'system_default',
    cv: 'system_default',
    case_study: 'system_default',
  });

  useEffect(() => {
    if (account?.uid && account.clientId) {
      getAllTemplates(account.uid, account.clientId)
        .then(setAvailableTemplates)
        .catch(() => {
          /* ignore */
        });
    } else if (account?.uid) {
      getUserTemplates(account.uid)
        .then(setAvailableTemplates)
        .catch(() => {
          /* ignore */
        });
    }
  }, [account?.uid, account?.clientId]);

  useEffect(() => {
    if (tender) {
      type TemplateType = 'draft' | 'cv' | 'case_study';
      const templateIds = tender.templateIds || EMPTY_OBJECT;

      const getDefault = (type: TemplateType): string => {
        const override = templateIds[type];
        if (override) {
          return override;
        }
        const userDefault = availableTemplates.find((t) => t.type === type && t.isDefault);
        if (userDefault) {
          return userDefault.id;
        }
        return 'system_default';
      };

      setSelectedTemplates({
        draft: getDefault('draft'),
        cv: getDefault('cv'),
        case_study: getDefault('case_study'),
      });
    }
  }, [tender, availableTemplates]);

  const handleTemplateChange = async (type: string, templateId: string) => {
    if (!tender?.id) {
      return;
    }
    setSelectedTemplates((prev) => ({ ...prev, [type]: templateId }));

    try {
      if (templateId !== 'system_default') {
        await setTenderTemplate(tender.id, templateId, type);
        showSnackbar('Template updated for this tender', 'success');
        logTenderActivity.updated({
          id: tender.id,
          title: tender.title || '',
          updateType: 'template_change',
          reason: `Changed ${type} template to ${templateId}`,
        });
      } else {
        await clearTenderTemplate(tender.id, type);
        showSnackbar('Template reset to system default', 'success');
        logTenderActivity.updated({
          id: tender.id,
          title: tender.title || '',
          updateType: 'template_change',
          reason: `Reset ${type} template to default`,
        });
      }
    } catch (_err) {
      showSnackbar('Failed to set template', 'error');
    }
  };

  return {
    availableTemplates,
    selectedTemplates,
    handleTemplateChange,
  };
};
